import {useCallback, useEffect, useMemo, useState} from 'react';
import {useQuery} from 'react-query';
import {ApiClientService} from '@dofleini/security';
import {useLocation} from 'react-router-dom';
import {getSearch} from '@/contexts/FilterContext';

export const createPaginationHook = (name, service, filtersKey, staleTime, enableQuery = undefined) => {
  let asyncFn;

  switch (typeof service) {
    case 'function':
      asyncFn = service;
      break;
    case 'string':
      asyncFn = params => ApiClientService.post(service, params);
      break;
    default:
      throw new TypeError('Invalid type of service');
  }

  return (search, filters, config = {}, queryConfig = {}, more = {}) => {
    const {search: locSearch, state} = useLocation();
    const [size, setSize] = useState(config.size || 10);
    const [sort, setSort] = useState(config.sort);

    const searchParams = useMemo(() => {
      return new URLSearchParams(locSearch);
    }, [locSearch]);

    const enabled = useCallback((filters) => {
      if(state && !filters) {
        return false;
      }
      if (enableQuery) {
        return enableQuery(filters);
      }
      return true;
    }, [state]);

    const pageFromUrl = parseInt(searchParams.get('page')) || 1;
    const [page, setPage] = useState(pageFromUrl || 1);

    const fetchData = useCallback(async (page = 1) => {
      // Ensure search is always a plain string. If `search` is an object keyed by module,
      // normalize it using the container `name` so the API receives `{ search: "text" }`.
      const normalizedSearch = getSearch(search, name);
      const {data, headers, request} = await asyncFn({page, size, search: normalizedSearch, filters, sort, ...more});
      const requestTotal = JSON.parse(request?.responseText);
      const totalPages = Number(headers['x-total-count'] || requestTotal?.total );
      const hasMore = data.length === size;
      return {data, totalPages, hasMore};
    }, [size, search, filters, sort, more]);

    const {
      status,
      error,
      isFetching,
      isLoading,
      isError,
      refetch,
      data = {},
    } = useQuery([name, page, getSearch(search, name), size, sort, filtersKey || filters, more], () => fetchData(page), {
      staleTime,
      ...queryConfig,
      keepPreviousData: true,
      enabled: enabled(filters),
    });

    useEffect(() => {
      if (!data?.data?.length && page > 1) {
        setPage(1);
      }
    }, [page, data]);

    return {
      page,
      status,
      ...data,
      error,
      isFetching,
      isLoading,
      isError,
      refetch,
      setPage,
      setSize,
      setSort,
      size,
      activeKey: [name, page, getSearch(search, name), size, filters]
    };
    // Prefetch the next page!
  };
};
